import { useGetMillwrightTasksByOutageIdQuery } from '@/apis/tasks/tasksApi';
import {
  LoadingSpinner,
  Stack,
  Typography,
  useWindowSize,
} from '@data-products-and-ai/react-components';
import { FC } from 'react';
import { MillwrightTask } from '@/pages/Millwrights/[ID]/components/MillwrightTasks/MillwrightTask';
import { usePagination } from '@/hooks';
import { Pagination } from '@/components';
import {
  INITIAL_PAGE,
  INITIAL_PAGE_SIZE_MEDIUM,
  SPINNER_SIZE,
  TABLET_WIDTH,
} from '@/constants';

import '@/pages/Millwrights/[ID]/components/MillwrightTasks/MillwrightTasks.scss';

interface MillwrightTasksProps {
  millwrightId: number;
  outageId: number;
}

export const MillwrightTasks: FC<MillwrightTasksProps> = ({
  millwrightId,
  outageId,
}) => {
  const { width } = useWindowSize();
  const isSmallerScreen = width <= TABLET_WIDTH;

  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: INITIAL_PAGE_SIZE_MEDIUM,
  });

  const { data, isLoading, isFetching } = useGetMillwrightTasksByOutageIdQuery(
    {
      millwrightId,
      outageId,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    },
    { skip: !millwrightId || !outageId },
  );

  const tasks = data?.results || [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const isLoadingState = isLoading || isFetching;

  const handlePageChange = (page: number) => {
    setPage(page);

    if (isSmallerScreen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="millwright-tasks">
      <Stack gap={16} direction="row" horizontalAlign="center">
        <Typography color="var(--color-black)" tag="h3">
          Tasks
        </Typography>
        <Typography color="var(--color-default)" tag="textdefault">
          {totalItems}
        </Typography>
      </Stack>
      {isLoadingState ? (
        <LoadingSpinner
          size={SPINNER_SIZE}
          text="Loading tasks..."
          color="var(--color-primary)"
          align="center"
          tag="textdefault"
          inline
        />
      ) : tasks.length > 0 ? (
        <Stack gap={36}>
          <div className="millwright-tasks__items">
            {tasks.map((task) => (
              <MillwrightTask
                key={`${task.taskID}-${task.dateWorked}`}
                outageId={outageId}
                task={task}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <Stack>
              <Pagination
                disabled={isFetching}
                currentPage={currentPage}
                totalPages={totalPages}
                maxVisiblePages={isSmallerScreen ? 3 : 5}
                onPageChange={handlePageChange}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack padding={32}>
          <Typography color="var(--color-black)" tag="textdefault_strong">
            No tasks available
          </Typography>
        </Stack>
      )}
    </div>
  );
};
